import React from 'react';
import { ArrowRight, CheckCircle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { solutions as practices } from '../data/solutions';

export const Solutions = () => {

  return (
    <div className="solutions-page pt-20">
      {/* Hero Section */}
      <section className="solutions-hero py-24 bg-gradient-to-br from-black via-gray-900 to-black relative overflow-hidden">
        <div className="absolute inset-0 hero-grid opacity-30" />
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl sm:text-6xl font-bold text-white mb-6 leading-tight">
              Three distinct solutions.<br />
              <span className="text-gradient">One shared purpose</span>
            </h1>
            <p className="text-xl text-gray-400 mb-4 leading-relaxed">
              Helping you scale further and sustain it longer.
            </p>
            <p className="text-base sm:text-lg text-gray-400 max-w-3xl mx-auto leading-relaxed">
              Every Radius solution is built on deep marketing and brand expertise, accelerated by in-house AI execution. Whether you need a single service or an integrated programme, you get the same rigour and the same ambition.
            </p>
          </div>
        </div>
      </section>

      {/* Practices */}
      <section className="practices py-24 bg-gray-900">
        <div className="max-w-7xl mx-auto px-6 space-y-24">
          {practices.map((practice, index) => (
            <div key={practice.id} className="practice-block">
              
              {/* Practice Image Header with Overlay */}
              <div className="relative h-96 overflow-hidden rounded-2xl mb-12">
                <img 
                  src={practice.image} 
                  alt={practice.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/70 to-gray-900" />
                
                {/* Text Overlay */}
                <div className="absolute inset-0 flex flex-col justify-center items-start px-12">
                  <span className="text-red-500 font-mono text-sm font-semibold mb-4">
                    {practice.number}
                  </span>
                  <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-4 max-w-4xl">
                    {practice.title}
                  </h2>
                  <p className="text-xl sm:text-2xl text-red-400 italic font-light max-w-3xl">
                    "{practice.tagline}"
                  </p>
                </div>
              </div>

              {/* Practice Description */}
              <div className="mb-12 px-2">
                <p className="text-base sm:text-lg text-gray-400 leading-relaxed max-w-4xl">
                  {practice.description}
                </p>
              </div>

              {/* Services Grid */}
              <div className="space-y-6">
                <h3 className="text-white font-semibold text-lg mb-6 px-2">Services</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  {practice.services.map((service, idx) => (
                    <Card key={idx} className="service-item-card group hover-lift">
                      <CardContent className="p-8">
                        <div className="flex items-start gap-3 mb-4">
                          <CheckCircle className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" />
                          <h4 className="text-xl font-bold text-white group-hover:text-gradient transition-all">
                            {service.name}
                          </h4>
                        </div>
                        <p className="text-gray-400 text-sm leading-relaxed ml-9">
                          {service.description}
                        </p>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Deep-link into the full solution page */}
                <div className="pt-4 px-2">
                  <Link
                    to={`/solutions/${practice.slug}`}
                    data-testid={`solution-detail-link-${practice.slug}`}
                    className="inline-flex items-center gap-2 text-red-500 hover:text-red-400 font-semibold transition-colors group/link"
                  >
                    Explore {practice.title}
                    <ArrowRight className="w-4 h-4 group-hover/link:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </div>

              {/* Divider - except for last practice */}
              {index < practices.length - 1 && (
                <div className="mt-20 pt-8 border-t border-gray-800" />
              )}
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="solutions-cta py-24 bg-black">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
            Ready to Scale Smarter?
          </h2>
          <p className="text-lg sm:text-xl text-gray-400 mb-10 leading-relaxed">
            Let's talk about which solution — or combination — makes sense for where your business is right now.
          </p>
          <Link to="/contact">
            <Button size="lg" className="btn-primary group">
              Connect
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Solutions;
